import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  GetApp as ExportIcon,
  Download as DownloadIcon,
  Timeline as TimelineIcon,
  BarChart as BarChartIcon,
  People as PeopleIcon,
  Phone as PhoneCallIcon
} from '@mui/icons-material';
import { analyticsService } from '../services/api';
import MetricsCards from '../components/Analytics/MetricsCards';
import CallTrendsChart from '../components/Analytics/CallTrendsChart';
import CallReasonsChart from '../components/Analytics/CallReasonsChart';
import AgentPerformanceTable from '../components/Analytics/AgentPerformanceTable';
import RecentCallsTable from '../components/Analytics/RecentCallsTable';
import TimeRangeSelector from '../components/Analytics/TimeRangeSelector';
import { exportToCSV, exportToJSON, prepareCallDataForExport, prepareAnalyticsDataForExport } from '../components/Analytics/ExportUtils';

const AnalyticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('30d'); // 7d, 30d, 90d, custom
  const [customDateRange, setCustomDateRange] = useState({
    startDate: null,
    endDate: null
  });
  const [analyticsData, setAnalyticsData] = useState({
    summary: {},
    trends: [],
    reasons: [],
    agentPerformance: [],
    recentCalls: []
  });
  const [exportAnchorEl, setExportAnchorEl] = useState(null);

  const getDateRangeParams = useCallback(() => {
    let end = new Date();
    let start = new Date();

    switch (timeRange) {
      case '7d':
        start.setDate(end.getDate() - 7);
        break;
      case '30d':
        start.setDate(end.getDate() - 30);
        break;
      case '90d':
        start.setDate(end.getDate() - 90);
        break;
      case 'custom':
        if (customDateRange.startDate && customDateRange.endDate) {
          start = new Date(customDateRange.startDate);
          end = new Date(customDateRange.endDate);
        }
        break;
      default:
        start.setDate(end.getDate() - 30);
    }

    return {
      startDate: start.toISOString(),
      endDate: end.toISOString()
    };
  }, [timeRange, customDateRange]);

  const fetchAnalyticsData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = getDateRangeParams();
      
      // Fetch all analytics data in parallel
      const [summary, trends, reasons, agentPerformance, recentCalls] = await Promise.all([
        analyticsService.getSummary(params),
        analyticsService.getTrends(params),
        analyticsService.getReasons(params),
        analyticsService.getAgentPerformance(params),
        analyticsService.getRecentCalls({ limit: 10 })
      ]);

      setAnalyticsData({
        summary,
        trends,
        reasons,
        agentPerformance,
        recentCalls
      });
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [getDateRangeParams]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  const handleRefresh = () => {
    fetchAnalyticsData();
  };

  const handleTimeRangeChange = (newRange) => {
    setTimeRange(newRange);
  };

  const handleCustomDateChange = (startDate, endDate) => {
    setCustomDateRange({ startDate, endDate });
    setTimeRange('custom');
  };

  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const handleExportCSV = () => {
    const callData = prepareCallDataForExport(analyticsData.recentCalls);
    exportToCSV(callData, `call-analytics-${timeRange}.csv`);
    handleExportClose();
  };

  const handleExportJSON = () => {
    const analyticsDataForExport = prepareAnalyticsDataForExport(analyticsData);
    exportToJSON(analyticsDataForExport, `analytics-${timeRange}.json`);
    handleExportClose();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
            Analytics Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track your call performance and agent analytics
          </Typography>
        </Box>
        
        <Box display="flex" alignItems="center" gap={2}>
          <TimeRangeSelector
            timeRange={timeRange}
            customDateRange={customDateRange}
            onTimeRangeChange={handleTimeRangeChange}
            onCustomDateChange={handleCustomDateChange}
          />
          
          <Tooltip title="Export Data">
            <IconButton onClick={handleExportClick} color="primary">
              <ExportIcon />
            </IconButton>
          </Tooltip>
          
          <Menu
            anchorEl={exportAnchorEl}
            open={Boolean(exportAnchorEl)}
            onClose={handleExportClose}
          >
            <MenuItem onClick={handleExportCSV}>
              <DownloadIcon sx={{ mr: 1 }} />
              Export as CSV
            </MenuItem>
            <MenuItem onClick={handleExportJSON}>
              <DownloadIcon sx={{ mr: 1 }} />
              Export as JSON
            </MenuItem>
          </Menu>
          
          <Tooltip title="Refresh data">
            <IconButton onClick={handleRefresh} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Metrics Cards */}
      <Box mb={4}>
        <MetricsCards data={analyticsData.summary} />
      </Box>

      {/* Charts Row */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} lg={8}>
          <Paper elevation={2} sx={{ p: 3, height: 400 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <TimelineIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" component="h2">
                Call Trends
              </Typography>
            </Box>
            <CallTrendsChart data={analyticsData.trends} />
          </Paper>
        </Grid>
        
        <Grid item xs={12} lg={4}>
          <Paper elevation={2} sx={{ p: 3, height: 400 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <BarChartIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" component="h2">
                Call Reasons
              </Typography>
            </Box>
            <CallReasonsChart data={analyticsData.reasons} />
          </Paper>
        </Grid>
      </Grid>

      {/* Tables Row */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" component="h2">
                Agent Performance
              </Typography>
            </Box>
            <AgentPerformanceTable data={analyticsData.agentPerformance} />
          </Paper>
        </Grid>
        
        <Grid item xs={12} lg={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <PhoneCallIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" component="h2">
                Recent Calls
              </Typography>
            </Box>
            <RecentCallsTable data={analyticsData.recentCalls} />
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AnalyticsPage; 